import { motion } from "framer-motion";
import { Award, Globe, TrendingUp, Compass, Target, Route } from "lucide-react";
import { SocialLinks } from "@/components/SocialLinks";
import { SiteHeader } from "@/components/SiteHeader";
import { PhotoSlideshow } from "@/components/PhotoSlideshow";

const JOURNEY_PHOTOS = [
  { src: "/about/norway3.jpg", alt: "Rhea Sharma at a Norwegian fjord summit", caption: "Norway. Above the clouds, between deployments." },
  { src: "/about/bolton.jpg", alt: "Rhea Sharma during her global immersion in the United Kingdom", caption: "The United Kingdom. Global immersion, 2019." },
  { src: "/about/norway1.jpg", alt: "Rhea Sharma hiking in Norway", caption: "Client onsite, then the mountains." },
  { src: "/about/norway2.jpg", alt: "Rhea Sharma at the edge of a fjord in Norway", caption: "Still climbing. Still curious." },
];

const FATHER_IMG = "/about/father.jpg";

const PURPOSE = [
  {
    id: "vision",
    icon: Compass,
    label: "Vision",
    text: "A world where no student or professional has to guess their next career move alone.",
  },
  {
    id: "mission",
    icon: Target,
    label: "Mission",
    text: "Turn potential into an executable career strategy.",
  },
  {
    id: "objective",
    icon: Route,
    label: "Objective",
    text: "Guide you end to end. Education. Work. Transitions. Until the next move is clear.",
  },
];

const HERO_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_career-clarity-hub-5/artifacts/a78dadm3_IMG_5743.jpg";
const BANNER_IMG = "/top100-poster.jpg";

const fade = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
};

const FOCUS = [
  { id: "clarity", title: "Career Clarity & Strategy", desc: "Tailored roadmaps for complex professional transitions and industry switches." },
  { id: "study-abroad", title: "Study Abroad Guidance", desc: "End to end strategic planning for students pursuing international education." },
  { id: "application", title: "Application Essentials", desc: "Professional resume development and optimization." },
  { id: "interview", title: "Interview Preparation", desc: "Focused coaching to help candidates successfully secure competitive roles." },
];

const Stat = ({ icon: Icon, value, label }) => (
  <div className="border border-[#E5E7EB] p-6">
    <Icon size={20} className="text-[#D94833]" />
    <p className="font-head text-3xl font-black mt-3">{value}</p>
    <p className="text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mt-1">{label}</p>
  </div>
);

export default function About() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SiteHeader />

      {/* Vision / Mission / Objective — company tagline */}
      <section className="bg-[#041C3E] text-white" data-testid="about-purpose">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">The Next Move by Rhellex</p>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-8">
            {PURPOSE.map((item, i) => (
              <motion.div
                key={item.id}
                {...fade}
                transition={{ ...fade.transition, delay: 0.1 * (i + 1) }}
                data-testid={`about-purpose-${item.id}`}
                className="lg:border-l-2 lg:border-[#D94833] lg:pl-6"
              >
                <div className="flex items-center gap-2">
                  <item.icon size={18} className="text-[#D94833]" />
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D94833]">{item.label}</p>
                </div>
                <p className="font-head text-2xl md:text-[1.75rem] font-black leading-[1.2] mt-4">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-16 lg:py-24">

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div {...fade} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">About</p>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-black mt-4">
              About The Next Move by Rhellex
            </h1>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed mt-6">
              The Next Move by Rhellex, a proud part of the KGSKF group, was founded on a distinct observation:
              highly capable professionals and students often lack a dedicated guide to help them navigate their
              critical career transitions.
            </p>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed mt-4">
              Built for those who need actionable strategy rather than just another pep talk, the organization
              serves as a definitive partner in professional and academic progress.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#041C3E]">Rhea Sharma, PMP® · MBA</span>
              <SocialLinks tone="light" />
            </div>
          </motion.div>

          <motion.div {...fade} transition={{ ...fade.transition, delay: 0.12 }} className="lg:col-span-5">
            <img src={HERO_IMG} alt="Rhea Sharma, career strategist and mentor"
                 className="w-full aspect-[4/5] object-cover object-[60%_center] border border-[#E5E7EB]" />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
          <Stat icon={Award} value="Top 100" label="DAP Professional 2024 & 2026" />
          <Stat icon={TrendingUp} value="7 years" label="Consulting experience" />
          <Stat icon={Globe} value="Global" label="India · U.K. · Norway · U.S." />
        </div>

        <motion.div {...fade} className="mt-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Recognition</p>
          <img src={BANNER_IMG} alt="Rhea Sharma, recognized as a Top 100 DAP Professional in 2024 and 2026"
               className="w-full h-auto border border-[#E5E7EB] mt-4" />
          <div className="mt-4 bg-[#041C3E] border border-[#041C3E] p-4 sm:p-6" data-testid="about-dap-profile">
            <img src="/dap-profile-1.png"
                 alt="Rhea Sharma, WalkMe Certified Digital Adoption Consultant and Advisor, with Program Manager, DAP Consultant, DAP Advisor, DAP Delivery Consultant, DAP Delivery Engineer, Builder and Fundamentals certifications, plus Whatfix Technology Advocate and Oracle Foundations Associate"
                 className="w-full h-auto" />
            <p className="text-xs text-white/60 mt-3 uppercase tracking-[0.15em]">
              Certified Digital Adoption Consultant and Advisor · WalkMe, Whatfix and Oracle credentials
            </p>
          </div>
        </motion.div>

        <div className="mt-16 border-t border-[#E5E7EB] pt-12 space-y-16">
          <div data-testid="about-founder-journey" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">The founder's journey</p>
              <h2 className="font-head text-2xl md:text-3xl lg:text-4xl font-black mt-3 leading-[1.1]">
                I did not wake up one morning and decide to become a coach.
              </h2>
              <div className="mt-6 space-y-4 text-base md:text-lg text-[#3A4A63] leading-relaxed max-w-2xl">
                <p>
                  I spent 7 years in corporate rooms and consulting rooms watching the same thing. Smart people.
                  Real potential. And no one whose only job was to help them decide the next step.
                </p>
                <p className="font-semibold text-[#041C3E]">I have been that person too.</p>
                <p className="font-head font-bold text-[#041C3E] leading-relaxed">
                  MBA. PMP. Instructional designer. Social media manager. Digital adoption. L&amp;D. Communications.
                  Operations. Content strategy. Transformation. India. The U.K. Norway. Now the U.S.
                </p>
                <p>
                  MBA at Symbiosis Institute of Management Studies. In 2019, a scholarship for the global immersion
                  program took me to Leeds Beckett University in the United Kingdom. Then Norway. Client onsite. UAT.
                  DAP deployment. Then the U.S. as a Senior Digital Adoption Manager. WalkMe named me a Top Digital
                  Adoption Platform Professional. That is the résumé version.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 w-full">
              <PhotoSlideshow photos={JOURNEY_PHOTOS} className="w-full aspect-[4/5] border border-[#E5E7EB]" testId="about-journey-slideshow" />
            </div>
          </div>

          <div className="max-w-2xl space-y-5">
            <p className="font-head text-2xl md:text-3xl font-black text-[#041C3E]">Here is the human version.</p>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed">
              Every one of those rooms taught me something I now refuse to waste. Instructional design taught me how
              people actually learn. Social media taught me how a story travels. Consulting taught me how to find the
              real problem under the polite one. Digital adoption taught me that a tool is useless if nobody can use
              it. L&amp;D taught me that potential without a plan is just a nice feeling.
            </p>
            <p className="border-l-4 border-[#D94833] pl-5 font-head text-xl md:text-2xl font-bold text-[#041C3E]">
              Career growth is a strategy. It is not a lottery.
            </p>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed">
              That is why I built The Next Move by Rhellex. A part of the KGSKF group.
            </p>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed">
              I do not open campus doors the way a placement cell does. They bring opportunities. They connect
              recruiters. They run drives. They open the door. Walking through it is still your job. I do not promise
              you a job. I equip you to earn one. I do not do the work for you. I build you for the work.
            </p>
            <div className="flex flex-wrap gap-2">
              {["Clarity", "Positioning", "Preparation", "Execution", "Accountability"].map((w) => (
                <span key={w} className="text-xs font-bold uppercase tracking-[0.15em] text-[#041C3E] bg-[#F7F5F2] px-3 py-2">{w}</span>
              ))}
            </div>
            <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed">
              College students. Fresh graduates. Junior professionals. Career switches. Study abroad. Interviews.
              LinkedIn. Resumes. International moves. If you are stuck on "what next," this is the room.
            </p>
          </div>

          <div data-testid="about-foundation" className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 w-full order-2 lg:order-1">
              <img src={FATHER_IMG} alt="Rhea Sharma as a child with her father, Lt. Commander Krishan Gopal, in his naval uniform"
                   className="w-full object-cover border border-[#E5E7EB]" />
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Our foundation and inspiration</p>
              <h2 className="font-head text-2xl md:text-3xl font-black mt-3 leading-[1.15]">
                The values did not start in those offices. They started at home.
              </h2>
              <p className="text-base md:text-lg text-[#3A4A63] leading-relaxed mt-5">
                A huge shout out to my father. Retd. Indian Naval Officer, Lt. Commander Krishan Gopal. Merchant Navy
                captain. Maritime professional. Then a corporate career as Senior VP at Standard Chartered Bank, HSBC
                and Microsoft. A visionary. A leader. His discipline, his standards, his way of showing up. Combined
                with my certifications, my years, my scars, and everything I had to figure out the hard way.
              </p>
              <p className="mt-5 border-l-4 border-[#D94833] pl-5 font-head text-xl md:text-2xl font-bold text-[#041C3E]">
                That mix is the offer.
              </p>
              <p className="mt-5 font-head text-lg md:text-xl font-black text-[#D94833]">
                We are live. Act now because every opportunity comes at a cost!
              </p>
            </div>
          </div>

          <div data-testid="about-what-we-do">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">What we do</p>
            <h2 className="font-head text-2xl md:text-3xl font-bold mt-3">Targeted, practical support.</h2>
            <p className="text-base text-[#3A4A63] leading-relaxed mt-4 max-w-3xl">
              Whether you are a student planning an international education or a professional pivoting to a new
              industry, The Next Move provides targeted, practical support. Core focus areas include:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              {FOCUS.map((f) => (
                <div key={f.id} data-testid={`about-focus-${f.id}`}
                     className="border border-[#E5E7EB] p-6 hover:border-[#041C3E] transition-colors duration-300">
                  <p className="font-head text-lg font-bold text-[#041C3E]">{f.title}</p>
                  <p className="text-sm text-[#3A4A63] leading-relaxed mt-2">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <a href="/#pricing" data-testid="about-services-link"
             className="bg-[#D94833] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#C03F2A] transition-colors duration-300">
            Explore the programs
          </a>
          <a href="/" className="border border-[#041C3E] text-[#041C3E] px-8 py-4 font-semibold tracking-wide hover:bg-[#041C3E] hover:text-white transition-colors duration-300">
            Back to home
          </a>
        </div>
      </main>

      <footer className="bg-[#041C3E] text-white py-12">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <p className="font-head font-black text-xl">The Next Move<span className="text-[#D94833]"> by Rhellex</span></p>
          <div data-testid="footer-contact" className="md:text-center">
            <p className="text-white text-sm font-semibold">Contact Rhea</p>
            <a href="mailto:thenextmovebyrhellex@gmail.com" className="text-white/60 text-sm hover:text-white transition-colors">thenextmovebyrhellex@gmail.com</a>
          </div>
          <SocialLinks tone="dark" />
        </div>
      </footer>
    </div>
  );
}
